import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import {
  Home, BookOpen, Calendar, TrendingUp, Trophy, MessageCircle, Youtube,
  Mail, DollarSign, Library, Activity, Send, KeyRound, Settings, Sun,
  ShieldPlus, Sparkles, Users as UsersIcon, Baby, Music,
  BookMarked, Search as SearchIcon,
} from 'lucide-react';
import { OT_TRACKER_BOOKS, NT_TRACKER_BOOKS } from '../data/bibleBookVideos';

/**
 * GlobalSearch — command-palette-style dialog for jumping to any part of the app.
 * Opens from the search icon in Navigation or via the Cmd/Ctrl+K keyboard shortcut.
 * Each item has keywords so common phrases ("audio bible", "praise report") match
 * even when they don't appear in the visible label.
 */

// Route index — labels the user might type, plus keywords for fuzzy matching.
// `to` is a react-router path; `query` optionally appends a query string
// (used for jumping straight to a tab, e.g. /prayers?tab=groups).
const ITEMS = [
  { label: 'Dashboard', desc: 'Your home + daily verse', icon: Home, to: '/dashboard', keywords: 'home overview welcome' },
  { label: 'Vital Signs Journal', desc: 'Daily spiritual reflection', icon: Activity, to: '/journal', keywords: 'journal write diary entries vitals reflection' },
  { label: 'Weekly Check-In', desc: 'Rate your week', icon: Calendar, to: '/checkin', keywords: 'weekly stage progress rating' },
  { label: 'Discipleship Path', desc: 'Your growth journey', icon: TrendingUp, to: '/discipleship', keywords: 'path pathway explore engage extend equip stages alpha film series' },
  { label: 'Sermon Notes', desc: 'Notes from Sunday messages', icon: BookOpen, to: '/sermon', keywords: 'sermon preaching notes bible passage ai questions' },
  { label: 'Prayer Wall', desc: 'Share and pray for requests', icon: MessageCircle, to: '/prayers', keywords: 'prayer wall public urgent ongoing short term praise confidential answered' },
  { label: 'Answered Prayers', desc: 'Public praise reports', icon: Sparkles, to: '/prayers', keywords: 'answered praise report celebrate testimonies' },
  { label: 'My Groups', desc: 'Invite-only prayer & accountability', icon: UsersIcon, to: '/prayers?tab=groups', keywords: 'my groups invite code prayer group' },
  { label: 'Media', desc: 'Sermons, podcasts, Christian radio', icon: Youtube, to: '/livestream', keywords: 'media video sermon podcast youtube spotify livestream' },
  { label: 'Audio Bible', desc: 'Listen to the Word', icon: BookMarked, to: '/livestream', keywords: 'audio bible listen bible.is scripture' },
  { label: 'Christian Radio', desc: 'MyFLR.org stream', icon: Music, to: '/livestream', keywords: 'radio myflr music stream christian' },
  { label: 'Resources', desc: 'Bible search, guides, timeline', icon: Library, to: '/resources', keywords: 'resources bible search ai guides pdf timeline alpha' },
  { label: 'Parent Resources', desc: 'Family faith activities', icon: Baby, to: '/resources?tab=parent', keywords: 'parent kids family children activities curriculum' },
  { label: 'Daily Devotionals', desc: 'Bible reading + reflection', icon: Sun, to: '/daily-devotionals', keywords: 'devotional daily reading reflection scripture' },
  { label: 'Tracker & Gamification', desc: 'Streaks, badges, leaderboard', icon: Trophy, to: '/gamification', keywords: 'tracker streak points badge achievement leaderboard level challenge mission' },
  { label: 'Give', desc: 'Online giving', icon: DollarSign, to: '/giving', keywords: 'give donation tithe offering' },
  { label: 'Messages', desc: 'Church-wide messages', icon: Mail, to: '/messages', keywords: 'messages inbox announcements' },
  { label: 'Contact Admin', desc: 'Send a private message to leaders', icon: Send, to: '/contact-admin', keywords: 'contact pastor admin private message question help' },
  { label: 'Notification Settings', desc: 'Control what pings you', icon: Settings, to: '/notifications', keywords: 'notifications settings push preferences enable disable' },
  { label: 'Security Questions', desc: 'Set up account recovery', icon: KeyRound, to: '/security-questions', keywords: 'security questions password recovery' },
];

const ADMIN_ITEMS = [
  { label: 'Admin Dashboard', desc: 'Manage everything', icon: ShieldPlus, to: '/admin', keywords: 'admin dashboard analytics management moderators sermons approval' },
  { label: 'Send Church Message', desc: 'Broadcast to all members', icon: Send, to: '/admin/messages', keywords: 'admin messages send broadcast announce' },
];

const norm = (s) => (s || '').toLowerCase();

const GlobalSearch = ({ open, onOpenChange, user }) => {
  const navigate = useNavigate();
  const { API } = React.useContext(AuthContext);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [sermonHits, setSermonHits] = useState([]);
  const [journalHits, setJournalHits] = useState([]);
  const [noteHits, setNoteHits] = useState([]);
  const inputRef = useRef(null);
  const analyticsTimer = useRef(null);

  const allItems = useMemo(() => {
    const list = [...ITEMS];
    if (user?.role === 'admin' || user?.is_prayer_moderator) {
      list.push(...ADMIN_ITEMS);
    }
    return list;
  }, [user?.role, user?.is_prayer_moderator]);

  const filteredNav = useMemo(() => {
    const q = norm(query).trim();
    if (!q) return allItems;
    const terms = q.split(/\s+/);
    return allItems.filter((it) => {
      const hay = `${norm(it.label)} ${norm(it.desc)} ${norm(it.keywords)}`;
      return terms.every((t) => hay.includes(t));
    });
  }, [query, allItems]);

  // Bible book overview matches (BibleProject) — pure client-side name match.
  const bibleBookHits = useMemo(() => {
    const q = norm(query).trim();
    if (q.length < 2) return [];
    const hits = [];
    OT_TRACKER_BOOKS.forEach((b) => {
      if (norm(b.name).includes(q)) {
        hits.push({
          label: `${b.name} — BibleProject Overview`,
          desc: 'Animated Old Testament book overview + Bible Gateway',
          icon: BookOpen,
          to: `/resources?tab=bible&openBooks=ot&book=${encodeURIComponent(b.name)}`,
        });
      }
    });
    NT_TRACKER_BOOKS.forEach((b) => {
      if (norm(b.name).includes(q)) {
        hits.push({
          label: `${b.name} — BibleProject Overview`,
          desc: 'Animated New Testament book overview + Bible Gateway',
          icon: BookOpen,
          to: `/resources?tab=bible&openBooks=nt&book=${encodeURIComponent(b.name)}`,
        });
      }
    });
    return hits.slice(0, 6);
  }, [query]);

  // Combined result list (used for keyboard navigation).
  const filtered = useMemo(() => {
    const sermonItems = sermonHits.map((s) => ({
      label: s.passage,
      desc: `Sermon · week of ${s.week_start_date}`,
      icon: BookOpen,
      to: `/sermon?date=${encodeURIComponent(s.week_start_date)}`,
      isSermon: true,
    }));
    const journalItems = journalHits.map((j) => ({
      label: j.snippet || 'Journal entry',
      desc: `Your journal · ${j.date}`,
      icon: Activity,
      to: `/journal?entry=${encodeURIComponent(j.id)}`,
    }));
    const noteItems = noteHits.map((n) => ({
      label: n.passage || 'Sermon note',
      desc: n.snippet ? `Your sermon note · ${n.snippet}` : `Your sermon note · ${n.week_start_date}`,
      icon: BookMarked,
      to: `/sermon?date=${encodeURIComponent(n.week_start_date)}`,
    }));
    return [...filteredNav, ...sermonItems, ...journalItems, ...noteItems, ...bibleBookHits];
  }, [filteredNav, sermonHits, journalHits, noteHits, bibleBookHits]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      setSermonHits([]);
      setJournalHits([]);
      setNoteHits([]);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => { setActiveIdx(0); }, [query]);

  // Debounced content search — hit backend endpoints in parallel when user pauses.
  useEffect(() => {
    const q = query.trim();
    if (!open) return;
    if (q.length < 2) {
      setSermonHits([]);
      setJournalHits([]);
      setNoteHits([]);
      return;
    }
    const t = setTimeout(async () => {
      const opts = { params: { q, limit: 5 } };
      // Fire all three in parallel — search palette stays snappy.
      const [sermons, journals, notes] = await Promise.all([
        axios.get(`${API}/sermons/search`, opts).then(r => r.data).catch(() => []),
        axios.get(`${API}/journal/search`, opts).then(r => r.data).catch(() => []),
        axios.get(`${API}/sermon-notes/search`, opts).then(r => r.data).catch(() => []),
      ]);
      setSermonHits(sermons || []);
      setJournalHits(journals || []);
      setNoteHits(notes || []);
    }, 240);
    return () => clearTimeout(t);
  }, [query, open, API]);

  // Fire-and-forget analytics: record what people search (once, ~800ms after
  // they stop typing), and record which result they clicked when they navigate.
  const logQuery = (extra = {}) => {
    if (!query.trim() || query.trim().length < 2) return;
    axios.post(`${API}/search-analytics`, {
      query: query.trim(),
      result_count: filtered.length,
      ...extra,
    }).catch(() => {});
  };

  useEffect(() => {
    if (!open) return;
    if (analyticsTimer.current) clearTimeout(analyticsTimer.current);
    if (query.trim().length < 2) return;
    analyticsTimer.current = setTimeout(() => logQuery(), 800);
    return () => { if (analyticsTimer.current) clearTimeout(analyticsTimer.current); };
  }, [query, open]); // eslint-disable-line

  const go = (item) => {
    // Record the click so we know which result the user picked.
    logQuery({ clicked_label: item.label, clicked_route: item.to });
    onOpenChange(false);
    const [path, search] = (item.to || '/').split('?');
    navigate(path + (search ? `?${search}` : ''));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = filtered[activeIdx];
      if (target) go(target);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0 gap-0" data-testid="global-search-dialog">
        <DialogHeader className="px-4 pt-4 pb-2 border-b border-slate-200">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold text-slate-800">
            <SearchIcon className="w-5 h-5 text-[#2B6B7F]" />
            Search the app
          </DialogTitle>
        </DialogHeader>
        <div className="px-4 py-3 border-b border-slate-100">
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Try 'audio bible', 'parent', 'my groups', 'praise'…"
            className="border-slate-200"
            data-testid="global-search-input"
          />
          <p className="text-xs text-slate-500 mt-1">
            Use ↑ ↓ to move, Enter to open. Cmd/Ctrl+K anywhere to reopen.
          </p>
        </div>
        <div className="max-h-[60vh] overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <p className="text-sm text-slate-500 italic text-center py-8" data-testid="global-search-empty">
              No matches. Try a different word.
            </p>
          ) : (
            (() => {
              const rows = [];
              const navCount = filteredNav.length;
              filteredNav.forEach((it, idx) => rows.push({ it, idx }));
              let running = navCount;

              const pushSection = (label, items, iconOverride) => {
                if (!items.length) return;
                rows.push({ heading: label, key: `${label}-heading` });
                items.forEach((raw, i) => {
                  rows.push({ it: raw, idx: running + i });
                });
                running += items.length;
              };

              pushSection('Sermons', sermonHits.map((s) => ({
                label: s.passage,
                desc: `Sermon · week of ${s.week_start_date}`,
                icon: BookOpen,
                to: `/sermon?date=${encodeURIComponent(s.week_start_date)}`,
              })));

              pushSection('Your Journal', journalHits.map((j) => ({
                label: j.snippet || 'Journal entry',
                desc: `${j.date}`,
                icon: Activity,
                to: `/journal?entry=${encodeURIComponent(j.id)}`,
              })));

              pushSection('Your Sermon Notes', noteHits.map((n) => ({
                label: n.passage || `Notes · ${n.week_start_date}`,
                desc: n.snippet || n.week_start_date,
                icon: BookMarked,
                to: `/sermon?date=${encodeURIComponent(n.week_start_date)}`,
              })));

              pushSection('Bible Book Overviews', bibleBookHits);

              return rows.map((row) => {
                if (row.heading) {
                  return (
                    <p
                      key={row.key}
                      className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400"
                    >
                      {row.heading}
                    </p>
                  );
                }
                const { it, idx } = row;
                const Icon = it.icon;
                const isActive = idx === activeIdx;
                return (
                  <button
                    key={`${it.label}-${idx}`}
                    type="button"
                    onClick={() => go(it)}
                    onMouseEnter={() => setActiveIdx(idx)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${isActive ? 'bg-[#2B6B7F]/8 text-[#2B6B7F]' : 'hover:bg-slate-50'}`}
                    data-testid={`global-search-result-${idx}`}
                  >
                    <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-[#2B6B7F]' : 'text-slate-500'}`} />
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium truncate ${isActive ? 'text-[#2B6B7F]' : 'text-slate-800'}`}>{it.label}</p>
                      {it.desc && <p className="text-xs text-slate-500 truncate">{it.desc}</p>}
                    </div>
                  </button>
                );
              });
            })()
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearch;
